/**
 * PhonebookEmployeeDetail.jsx — Modal mit Kontakt-Karte fuer einen Mitarbeiter.
 *
 * Blocks:
 *  1. Kontaktdaten (Festnetz/Mobil/E-Mail) als Click-to-Action
 *     + Standort-Adresse mit Maps-Link, Hauptnummer/Fax/E-Mail des Standorts
 *  2. Heute (Schicht/Urlaub/abwesend) + Wochen-Schichtleiste Mo-So
 *  3. Vorgesetzter (Standortleiter/in) + Team-Count + Vertretung (optional)
 *  4. Aktionen: vCard-Download, Termin-Mail
 *
 * Datenschutz: nur dienstliche Daten, kein Geburtsdatum, keine Krankheitsdetails.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PositionBadges } from './employees/PositionBadges';
import {
  Phone, Smartphone, Mail, MapPin, Building2, Users, Calendar, Loader2,
  ExternalLink, Download, CalendarPlus, Briefcase, UserCheck,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  present: { label: 'anwesend', dot: 'bg-emerald-500' },
  scheduled: { label: 'Schicht heute', dot: 'bg-blue-500' },
  off_duty: { label: 'Schicht beendet', dot: 'bg-amber-500' },
  absence: { label: 'abwesend', dot: 'bg-red-500' },
  free: { label: 'nicht im Dienst', dot: 'bg-slate-400' },
};

const initials = (first, last) =>
  ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const ContactBigButton = ({ icon: Icon, label, value, href, testId }) => {
  if (!value) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-3 py-2.5 text-left">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </div>
        <div className="text-sm text-muted-foreground/60 mt-0.5">—</div>
      </div>
    );
  }
  return (
    <a
      href={href}
      className="block rounded-md border border-border bg-card hover:border-primary hover:bg-primary/5 transition-colors px-3 py-2.5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-sm font-medium break-all mt-0.5">{value}</div>
    </a>
  );
};

const buildVCard = (data) => {
  const { employee, location } = data;
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${employee.last_name};${employee.first_name};;;`,
    `FN:${employee.first_name} ${employee.last_name}`.trim(),
  ];
  if (employee.position) lines.push(`TITLE:${employee.position}`);
  if (employee.department) lines.push(`ORG:${employee.department}`);
  if (employee.phone) lines.push(`TEL;TYPE=WORK,VOICE:${employee.phone}`);
  if (employee.mobile) lines.push(`TEL;TYPE=CELL,VOICE:${employee.mobile}`);
  if (employee.email) lines.push(`EMAIL;TYPE=WORK:${employee.email}`);
  if (location?.address) {
    const adr = `;;${location.address || ''};${location.city || ''};;${location.postal_code || ''};`;
    lines.push(`ADR;TYPE=WORK:${adr}`);
  }
  lines.push('END:VCARD');
  return lines.join('\r\n');
};

const downloadVCard = (data) => {
  const vcf = buildVCard(data);
  const blob = new Blob([vcf], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${data.employee.last_name}_${data.employee.first_name}.vcf`.replace(/\s+/g, '_');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
  toast.success('vCard heruntergeladen');
};

const mapsHref = (loc) => {
  if (!loc) return '#';
  if (loc.latitude && loc.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`;
  }
  const q = [loc.address, loc.postal_code, loc.city].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const meetingMailHref = (email, name) => {
  if (!email) return null;
  const subject = encodeURIComponent('Terminanfrage');
  const body = encodeURIComponent(`Hallo ${name},\n\nich würde gerne einen Termin mit dir vereinbaren.\n\nViele Grüße`);
  return `mailto:${email}?subject=${subject}&body=${body}`;
};

export const PhonebookEmployeeDetail = ({ employeeId, open, onClose, onJumpToLocation }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    setData(null);
    api.get(`/phonebook/employees/${employeeId}/contact-card`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Kontaktkarte konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, employeeId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="phonebook-contact-card"
      >
        {loading || !data ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Kontaktkarte…
          </div>
        ) : (
          <>
            <DialogHeader className="flex-row items-start gap-4 space-y-0 border-b border-border/40 pb-4">
              <div
                className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg shrink-0"
                data-testid="contact-card-avatar"
              >
                {data.employee.avatar_url ? (
                  <img src={data.employee.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  initials(data.employee.first_name, data.employee.last_name)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="font-heading text-xl">
                  {data.employee.last_name}, {data.employee.first_name}
                </DialogTitle>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mt-1">
                  {(data.employee.position || (data.employee.position_list && data.employee.position_list.length > 0)) && (
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase className="h-3 w-3" />
                      <PositionBadges employee={data.employee} />
                    </span>
                  )}
                  <span
                    className="inline-flex items-center gap-1.5"
                    data-testid="contact-card-status"
                  >
                    <span className={`w-2 h-2 rounded-full ${STATUS_LABEL[data.today.status]?.dot || 'bg-slate-400'}`} />
                    {STATUS_LABEL[data.today.status]?.label || data.today.status}
                  </span>
                </div>
                {data.employee.department && (
                  <Badge variant="secondary" className="mt-2 text-[10px]">
                    {data.employee.department}
                  </Badge>
                )}
              </div>
            </DialogHeader>

            {/* Block 1: Kontakt */}
            <section className="space-y-2 pt-4">
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Kontakt</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <ContactBigButton
                  icon={Phone}
                  label="Festnetz"
                  value={data.employee.phone}
                  href={data.employee.phone ? `tel:${data.employee.phone.replace(/\s+/g, '')}` : '#'}
                  testId="contact-card-phone"
                />
                <ContactBigButton
                  icon={Smartphone}
                  label="Mobil"
                  value={data.employee.mobile}
                  href={data.employee.mobile ? `tel:${data.employee.mobile.replace(/\s+/g, '')}` : '#'}
                  testId="contact-card-mobile"
                />
                <ContactBigButton
                  icon={Mail}
                  label="E-Mail"
                  value={data.employee.email}
                  href={data.employee.email ? `mailto:${data.employee.email}` : '#'}
                  testId="contact-card-email"
                />
              </div>
            </section>

            {/* Standort */}
            {data.location && (
              <section className="space-y-2 pt-4">
                <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Standort</h4>
                <div className="rounded-md border border-border/60 p-3 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Building2 className="h-4 w-4 text-purple-600" />
                    {data.branch?.name ? `${data.branch.name} · ` : ''}{data.location.name}
                  </div>
                  {(data.location.address || data.location.city) && (
                    <a
                      href={mapsHref(data.location)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                      data-testid="contact-card-maps-link"
                    >
                      <MapPin className="h-3.5 w-3.5" />
                      {[data.location.address, [data.location.postal_code, data.location.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
                    {data.location.phone && (
                      <a href={`tel:${data.location.phone.replace(/\s+/g, '')}`} className="hover:text-primary">
                        Tel: {data.location.phone}
                      </a>
                    )}
                    {data.location.fax && <span>Fax: {data.location.fax}</span>}
                    {data.location.email && (
                      <a href={`mailto:${data.location.email}`} className="hover:text-primary">
                        {data.location.email}
                      </a>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Block 2: Heute & Woche */}
            <section className="space-y-2 pt-4">
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Heute & diese Woche
              </h4>
              <div className="rounded-md border border-border/60 p-3 space-y-3">
                <div className="text-sm" data-testid="contact-card-today">
                  {data.today.status === 'absence' && (
                    <span>
                      Abwesend
                      {data.today.absence_end && (
                        <> bis <strong>{data.today.absence_end}</strong></>
                      )}
                    </span>
                  )}
                  {data.today.shift && (data.today.status === 'present' || data.today.status === 'scheduled' || data.today.status === 'off_duty') && (
                    <span>
                      ▸ Schicht {data.today.shift.start_time}–{data.today.shift.end_time}
                      {data.today.shift.massnahme_name && <span className="text-muted-foreground"> · {data.today.shift.massnahme_name}</span>}
                    </span>
                  )}
                  {data.today.status === 'free' && <span className="text-muted-foreground">Heute nicht im Dienst</span>}
                </div>

                {/* Wochenleiste */}
                <div className="flex items-center gap-1 text-[10px]" data-testid="contact-card-week">
                  {data.week.days.map((d, i) => {
                    const onAbsence = d.on_absence ?? d.on_vacation;
                    const cls = onAbsence
                      ? 'bg-red-500 text-white'
                      : d.has_shift
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground';
                    return (
                      <div key={d.date} className="flex-1 text-center">
                        <div className="text-muted-foreground mb-1">{WEEKDAYS[i]}</div>
                        <div className={`rounded h-6 flex items-center justify-center font-medium ${cls}`}>
                          {onAbsence ? 'A' : d.has_shift ? '■' : '·'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> Abwesenheit</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-primary" /> Einsatz</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-muted-foreground/30" /> frei</span>
                </div>
              </div>
            </section>

            {/* Block 3: Vorgesetzter & Team */}
            <section className="space-y-2 pt-4">
              <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5" /> Vorgesetzter & Team
              </h4>
              <div className="rounded-md border border-border/60 p-3 space-y-2">
                {data.manager ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <div className="font-medium">{data.manager.name}</div>
                      <div className="text-xs text-muted-foreground">{data.manager.position}</div>
                    </div>
                    <div className="flex gap-1.5">
                      {data.manager.phone && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`tel:${data.manager.phone.replace(/\s+/g, '')}`}><Phone className="h-3.5 w-3.5" /></a>
                        </Button>
                      )}
                      {data.manager.mobile && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`tel:${data.manager.mobile.replace(/\s+/g, '')}`}><Smartphone className="h-3.5 w-3.5" /></a>
                        </Button>
                      )}
                      {data.manager.email && (
                        <Button size="sm" variant="outline" asChild>
                          <a href={`mailto:${data.manager.email}`}><Mail className="h-3.5 w-3.5" /></a>
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Standortleiter/in nicht hinterlegt</div>
                )}

                <div className="text-xs text-muted-foreground border-t border-border/40 pt-2">
                  Vertretung: <span className="italic">nicht hinterlegt</span>
                </div>

                {data.team_count > 0 && (
                  <button
                    type="button"
                    onClick={() => { onJumpToLocation?.(data.location?.id); onClose(); }}
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                    data-testid="contact-card-team-jump"
                  >
                    <Users className="h-3 w-3" />
                    {data.team_count} weitere{data.team_count === 1 ? 'r Kollege' : ' Kollegen'} am Standort
                  </button>
                )}
              </div>
            </section>

            {/* Block 4: Aktionen */}
            <section className="pt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => downloadVCard(data)}
                data-testid="contact-card-vcard-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                vCard (.vcf)
              </Button>
              {data.employee.email && (
                <Button
                  variant="outline"
                  asChild
                  data-testid="contact-card-meeting-btn"
                >
                  <a href={meetingMailHref(data.employee.email, data.employee.first_name)}>
                    <CalendarPlus className="mr-2 h-4 w-4" />
                    Termin per Mail
                  </a>
                </Button>
              )}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhonebookEmployeeDetail;
